<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { Icon } from "../../components";

// --- Helper functions (kept at module scope for general use) ---
const parseDateString = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  // Month is 0-indexed in JavaScript Date
  return new Date(year, month - 1, day);
};

const formatDateToString = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSameDate = (date1, date2) => {
  if (!date1 || !date2) return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};
// --- End of helper functions ---

const props = defineProps({
  initialStartDate: {
    type: String,
    default: null, // Changed default to null
  },
  initialSelectedDate: {
    type: String,
    default: null, // Changed default to null
  },
  disabledDates: {
    type: Array, // Array of strings 'YYYY-MM-DD'
    default: () => [],
  },
  availableDates: {
    type: Array, // Array of strings 'YYYY-MM-DD'
    default: () => [],
  },
});

const emit = defineEmits(["date-selected", "range-changed"]);

// Internal refs for Date objects
const startDate = ref(null);
const selectedDate = ref(null);

const daysOfWeek = (() => {
  const today = new Date().getDay();
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  // Convert Sunday (0) to 6 for proper rotation
  const todayIndex = today === 0 ? 6 : today - 1;

  // Calculate offset to put today in the middle (3 days before, today, 3 days after)
  const offset = 3;
  const adjustedIndex = (todayIndex + 7 - offset) % 7;

  // Rotate array so today is in the middle
  return [...days.slice(adjustedIndex), ...days.slice(0, adjustedIndex)];
})();

// Reactive month and year based on the current start date
const currentMonth = computed(() => startDate.value?.getMonth()); // Use optional chaining
const currentYear = computed(() => startDate.value?.getFullYear()); // Use optional chaining

const monthName = computed(() => {
  if (!startDate.value) return ""; // Handle case where startDate is null initially
  const date = new Date(currentYear.value, currentMonth.value, 1);
  return date.toLocaleString("en-US", { month: "long" });
});

const visibleDates = computed(() => {
  if (!startDate.value) return []; // Don't render dates until startDate is set

  const datesArray = [];
  let currentDate = new Date(startDate.value);

  const parsedDisabledDates = props.disabledDates
    .map(parseDateString)
    .filter(Boolean);
  const parsedAvailableDates = props.availableDates
    .map(parseDateString)
    .filter(Boolean);

  for (let i = 0; i < 7; i++) {
    const fullDate = new Date(currentDate);

    const isSelected =
      selectedDate.value && isSameDate(selectedDate.value, fullDate);

    const isDisabled = parsedDisabledDates.some((disabledDate) =>
      isSameDate(disabledDate, fullDate)
    );

    let isAvailable = true;
    if (parsedAvailableDates.length > 0) {
      isAvailable = parsedAvailableDates.some((availableDate) =>
        isSameDate(availableDate, fullDate)
      );
    }

    datesArray.push({
      fullDate: fullDate,
      dayOfMonth: fullDate.getDate(),
      selected: isSelected,
      disabled: isDisabled || !isAvailable,
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return datesArray;
});

const selectDate = (fullDate) => {
  const dateItem = visibleDates.value.find((item) =>
    isSameDate(item.fullDate, fullDate)
  );

  if (dateItem && !dateItem.disabled) {
    selectedDate.value = fullDate;
    emit("date-selected", fullDate);
  }
};

const navigateSevenDays = (direction) => {
  const newStartDate = new Date(startDate.value);
  newStartDate.setDate(newStartDate.getDate() + direction * 7);
  startDate.value = newStartDate;
  emit("range-changed", {
    startDate: newStartDate,
    endDate: new Date(
      newStartDate.getFullYear(),
      newStartDate.getMonth(),
      newStartDate.getDate() + 6
    ),
  });
};

// --- Watchers for props and initial setup logic ---

// Watch for changes in initialSelectedDate prop (string)
watch(
  () => props.initialSelectedDate,
  (newVal) => {
    selectedDate.value = parseDateString(newVal);
  },
  { immediate: true } // Run immediately on component mount
);

// Watch for changes in initialStartDate prop (string)
watch(
  () => props.initialStartDate,
  (newVal) => {
    startDate.value = parseDateString(newVal);
  },
  { immediate: true } // Run immediately on component mount
);

// Fallback for default dates if props are not provided
onMounted(() => {
  // If no initialStartDate was provided (or was null), set a default
  if (!props.initialStartDate) {
    const defaultStart = new Date();
    defaultStart.setDate(defaultStart.getDate() - 3);
    startDate.value = defaultStart;
  }

  // If no initialSelectedDate was provided (or was null), set a default (e.g., today)
  if (!props.initialSelectedDate) {
    selectedDate.value = new Date();
  }

  // Your original example logic (Jan 9, 2023 within Jan 6-12 range)
  // This part might be specific to your use case, and you can adjust it.
  // If you want to force Jan 9 if initialSelectedDate is not provided AND startDate is Jan 6
  if (
    !props.initialSelectedDate &&
    startDate.value &&
    isSameDate(startDate.value, new Date(2023, 0, 6))
  ) {
    selectedDate.value = new Date(2023, 0, 9);
  }
});
</script>

<template>
  <div class="date-picker-container">
    <div
      class="flex items-center gap-2 justify-between pb-[8px] mb-[8px] border-b-1 border-t-0 border-r-0 border-l-0 border-solid border-[#EAECF0]"
    >
      <h2 class="text-gray-700 text-[16px] m-0">
        {{ monthName }} {{ currentYear }}
      </h2>
      <div class="flex items-center gap-0.5">
        <button
          @click="navigateSevenDays(-1)"
          class="bg-white border-none cursor-pointer inline-flex p-0"
        >
          <Icon name="arrowLeft" class="text-gray-500 w-5 h-5" />
        </button>
        <button
          @click="navigateSevenDays(1)"
          class="bg-white border-none cursor-pointer inline-flex p-0"
        >
          <Icon name="arrowRight" class="text-gray-500 w-5 h-5" />
        </button>
      </div>
    </div>
    <div
      class="grid grid-cols-7 gap-1.5 text-[14px] text-gray-500 font-medium text-center py-[5px]"
    >
      <span v-for="day in daysOfWeek" :key="day">{{ day }}</span>
    </div>
    <div
      class="grid grid-cols-7 gap-1.5 text-[14px] text-gray-500 font-medium text-center pt-[5px]"
    >
      <div
        v-for="dateItem in visibleDates"
        :key="dateItem.fullDate.getTime()"
        :class="[
          'date-box',
          { selected: dateItem.selected, disabled: dateItem.disabled },
        ]"
        class="cursor-pointer py-[10px] border border-solid border-gray-200 rounded-[4px]"
        @click="selectDate(dateItem.fullDate)"
      >
        {{ dateItem.dayOfMonth }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.date-box.disabled {
  cursor: not-allowed !important;
  border: 0px solid !important;
}
.date-box.selected {
  /* background-color: #1a73e8; 
  color: white;
  font-weight: bold; */
}
.date-box:not(.disabled):hover {
  /* background-color: #e6f0ff; */
}
</style>
